import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, ProgressBar, Spinner, Avatar, Select } from "@/components/UI";
import { Zap, CheckCircle2, TrendingUp, Sparkles } from "lucide-react";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export default function SprintBoard() {
  const [sprints, setSprints] = useState([]);
  const [selected, setSelected] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sprints").then((r) => {
      setSprints(r.data);
      const active = r.data.find((s) => s.status === "active") || r.data[0];
      if (active) setSelected(String(active.id));
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setData(null);
    api.get(`/sprints/${selected}`).then((r) => setData(r.data)).catch(() => {});
  }, [selected]);

  const years = [...new Set(sprints.map((s) => s.year || (s.start_date ? new Date(s.start_date).getFullYear() : null)).filter(Boolean))].sort((a, b) => b - a);
  const filteredSprints = sprints.filter((s) => {
    const d = s.start_date ? new Date(s.start_date) : null;
    if (year && (!d || d.getFullYear() !== Number(year))) return false;
    if (month && (!d || d.getMonth() + 1 !== Number(month))) return false;
    return true;
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="sprint-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sprint Team</h1>
          <p className="text-sm text-slate-400">Workload & performa mingguan tim.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-28" value={year} onChange={(e) => setYear(e.target.value)} data-testid="sprint-team-filter-year">
            <option value="">Semua Tahun</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
          <Select className="w-36" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sprint-team-filter-month">
            <option value="">Semua Bulan</option>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </Select>
          <Select className="w-auto min-w-[220px]" value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="sprint-selector">
            {filteredSprints.map((s) => (
              <option key={s.id} value={s.id}>{s.name} {s.status === "active" ? "· ACTIVE" : ""}</option>
            ))}
          </Select>
        </div>
      </div>

      {!data ? (
        <Spinner />
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TotalCard icon={Zap} color="#D1D5DB" label="Dialokasikan" value={data.totals.total_allocated} unit="pts" />
            <TotalCard icon={CheckCircle2} color="#D1D5DB" label="Selesai (Output)" value={data.totals.total_completed} unit="pts" />
            <TotalCard icon={TrendingUp} color="#E5E7EB" label="Performance Tim" value={data.totals.performance ?? "N/A"} unit={data.totals.performance != null ? "%" : ""} />
          </div>

          {/* Employee capacity gauges */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {data.employees.map((e) => (
              <EmployeeCard key={e.user_id} e={e} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TotalCard({ icon: Icon, color, label, value, unit }) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
        <Icon size={14} style={{ color }} /> {label}
      </div>
      <p className="font-mono text-3xl font-bold tabular-nums" style={{ color }}>
        {value}<span className="ml-1 text-base text-slate-500">{unit}</span>
      </p>
    </Card>
  );
}

export function EmployeeCard({ e, sprintName, className }) {
  const perfColor = e.performance == null ? "#64748B" : e.performance >= 90 ? "#D1D5DB" : e.performance >= 60 ? "#E5E7EB" : "#F43F5E";
  return (
    <Card className={"p-5 " + (className || "")} data-testid={`sprint-employee-${e.user_id}`}>
      <div className="mb-4 flex items-center justify-between">
        {sprintName ? (
          <div className="flex items-center gap-2">
            <p className="font-heading font-semibold text-slate-100">{sprintName}</p>
            <span className="rounded-md bg-slate-500/15 px-2 py-0.5 text-[11px] font-semibold text-slate-400">ACTIVE</span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Avatar src={e.avatar_url} name={e.user_name} size={42} />
            <div>
              <p className="font-heading font-semibold text-slate-100">{e.user_name}</p>
              <p className="text-xs text-slate-500">{e.job_title}</p>
            </div>
          </div>
        )}
        <div className="text-right">
          <p className="font-heading text-2xl font-bold tabular-nums" style={{ color: perfColor }}>
            {e.performance == null ? "N/A" : e.performance + "%"}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Performance</p>
        </div>
      </div>

      {/* Allocated bar */}
      <div className="mb-3">
        <div className="mb-1 flex justify-between text-xs">
          <span className="text-slate-400">Bobot Dialokasikan</span>
          <span className="font-mono tabular-nums text-slate-400">{e.allocated} pts</span>
        </div>
        <ProgressBar value={e.allocated} max={Math.max(e.allocated, e.completed) || 1} color="#9CA3AF" height={6} />
      </div>

      {/* Completed bar */}
      <div className="mb-4">
        <div className="mb-1 flex justify-between text-xs">
          <span className="text-slate-400">Bobot Selesai (Output)</span>
          <span className="font-mono tabular-nums text-slate-400">{e.completed} pts</span>
        </div>
        <ProgressBar value={e.completed} max={Math.max(e.allocated, e.completed) || 1} color="#D1D5DB" height={6} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <MiniStat label="Output" value={e.output} color="#D1D5DB" />
        <MiniStat label="Sisa" value={e.remaining} color="#9CA3AF" />
        <MiniStat label="Extra" value={e.extra_contribution > 0 ? `+${e.extra_contribution}` : "0"} color="#D1D5DB" icon={e.extra_contribution > 0 ? Sparkles : null} />
      </div>

      {e.extra_contribution > 0 && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-2 text-xs text-slate-300">
          <Sparkles size={14} /> Kontribusi ekstra +{e.extra_contribution} poin
        </div>
      )}
    </Card>
  );
}

function MiniStat({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 py-2">
      <p className="flex items-center justify-center gap-1 font-mono text-lg font-bold tabular-nums" style={{ color }}>
        {Icon && <Icon size={13} />} {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}
